/**
 * Aba WhatsApp — versão simplificada.
 *
 * FILOSOFIA: o admin só precisa clicar em "Conectar meu WhatsApp",
 * escanear o QR e pronto. Tudo mais é escondido em "Configuração avançada".
 *
 * Estados:
 *  A) Auto-conexão INDISPONÍVEL (servidor Evolution não configurado):
 *     Mostra tela explicando que o modo automático não está pronto e
 *     oferece o modo manual (wa.me).
 *  B) Auto-conexão DISPONÍVEL, tenant desconectado:
 *     Botão gigante "Conectar meu WhatsApp".
 *  C) Auto-conexão DISPONÍVEL, tenant AGUARDANDO QR:
 *     QR grande + instruções passo-a-passo.
 *  D) Auto-conexão DISPONÍVEL, tenant conectado:
 *     Confirmação verde + número conectado + botão testar + botão desconectar.
 */
import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  MessageCircle, Loader2, Send, CheckCircle2, AlertCircle,
  Smartphone, RefreshCw, LogOut, Zap, ChevronDown, ChevronUp, Info, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const DEFAULT_CFG = {
  provider: "wa_link",
  sender_display: "",
  evolution: { base_url: "", instance_name: "", api_key: "" },
  cloud: { phone_number_id: "", access_token: "", business_account_id: "" },
};

export default function WhatsAppConfigTab() {
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // QR flow state
  const [autoAvailable, setAutoAvailable] = useState(false);
  const [qr, setQr] = useState(null);
  const [connState, setConnState] = useState(null);
  const [connNumber, setConnNumber] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const pollRef = useRef(null);

  const checkAvailable = async () => {
    try {
      const { data } = await api.get("/tenant/whatsapp/evolution/available");
      setAutoAvailable(!!data.available);
    } catch { /* silencia */ }
  };

  const checkState = async () => {
    try {
      const { data } = await api.get("/tenant/whatsapp/evolution/state");
      setConnState(data.state);
      setConnNumber(data.number);
      if (data.state === "open") {
        setQr(null);
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    } catch { /* silencia */ }
  };

  const startConnect = async () => {
    setQrLoading(true);
    try {
      const { data } = await api.post("/tenant/whatsapp/evolution/create-instance");
      setQr(data);
      toast.success("Escaneie o QR code com o WhatsApp da empresa");
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(checkState, 3000);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setQrLoading(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Desconectar o WhatsApp? Você precisará escanear o QR novamente para reconectar.")) return;
    try {
      await api.post("/tenant/whatsapp/evolution/disconnect");
      setConnState("close");
      setConnNumber(null);
      toast.success("WhatsApp desconectado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/whatsapp/config");
      setCfg({ ...DEFAULT_CFG, ...data,
        evolution: { ...DEFAULT_CFG.evolution, ...(data.evolution || {}) },
        cloud: { ...DEFAULT_CFG.cloud, ...(data.cloud || {}) },
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    checkAvailable();
    checkState();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const saveAdvanced = async () => {
    try {
      await api.put("/tenant/whatsapp/config", cfg);
      toast.success("Configuração salva!");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doTest = async () => {
    if (!testPhone) { toast.error("Informe um número de telefone"); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/tenant/whatsapp/test", {
        phone: testPhone,
        text: "Teste — mensagem automática da SUA PLATAFORMA. Conexão OK ✅",
      });
      setTestResult(data);
      if (data.ok) toast.success("Mensagem enviada com sucesso!");
      else toast.warning("Não conseguimos enviar. Veja o detalhe abaixo.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  if (loading) return <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;

  const isConnected = connState === "open";
  const isConnecting = qr && !isConnected;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="whatsapp-config-tab">
      <div>
        <h3 className="font-display font-semibold text-lg flex items-center gap-2">
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Conecte o WhatsApp da sua empresa para que o sistema envie lembretes,
          confirmações e mensagens de retorno <strong>automaticamente</strong>, sem você precisar clicar em nada.
        </p>
      </div>

      {/* Estado A: modo automático não disponível no servidor */}
      {!autoAvailable && (
        <Card className="p-5 rounded-tenant border-border" data-testid="wa-unavailable-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 grid place-items-center shrink-0">
              <Info className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="font-display font-semibold">Envio automático em breve</div>
              <p className="text-sm text-muted-foreground mt-1">
                Estamos preparando o envio automático. Enquanto isso, os botões de WhatsApp da plataforma abrem
                links prontos para você despachar do próprio celular em 1 toque — nenhuma mensagem se perde.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Estado B/C/D: fluxo principal */}
      {autoAvailable && (
        <Card
          className="p-6 rounded-tenant border-border space-y-5"
          data-testid="wa-auto-connect-card"
          style={{ background: "linear-gradient(135deg, hsl(var(--tenant-primary) / 0.06), transparent)" }}
        >
          {isConnected && (
            <div className="flex items-start justify-between gap-3" data-testid="wa-connected-block">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 grid place-items-center shrink-0">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-lg">WhatsApp conectado</div>
                  <div className="text-sm text-muted-foreground mt-0.5">
                    Envios automáticos ativos
                    {connNumber && <> no número <strong className="text-foreground font-mono">{connNumber}</strong></>}.
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    A cada agendamento, o sistema envia lembrete, confirmação e mensagem de retorno sozinho.
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={disconnect} className="gap-1.5 shrink-0" data-testid="wa-disconnect-btn">
                <LogOut className="w-3.5 h-3.5" /> Desconectar
              </Button>
            </div>
          )}

          {!isConnected && !isConnecting && (
            <div className="text-center py-6 space-y-4" data-testid="wa-connect-cta">
              <div className="w-16 h-16 rounded-2xl mx-auto grid place-items-center"
                style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}>
                <Smartphone className="w-8 h-8" />
              </div>
              <div>
                <div className="font-display font-semibold text-lg">Conecte seu WhatsApp em 30 segundos</div>
                <div className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                  Vamos gerar um QR code. Você escaneia com o WhatsApp da empresa e pronto — a partir daí
                  o sistema envia tudo sozinho.
                </div>
              </div>
              <Button
                onClick={startConnect}
                disabled={qrLoading}
                size="lg"
                className="gap-2"
                data-testid="wa-connect-btn"
                style={{ background: "hsl(var(--tenant-primary))", color: "hsl(var(--tenant-primary-foreground))" }}
              >
                {qrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Conectar meu WhatsApp
              </Button>
              <div className="text-[11px] text-muted-foreground flex items-center justify-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Seguro · reversível · não usa senha
              </div>
            </div>
          )}

          {isConnecting && (
            <div className="space-y-4" data-testid="wa-qr-block">
              <div className="grid md:grid-cols-[240px_1fr] gap-5 items-start">
                <div className="w-full aspect-square bg-white p-3 rounded-lg border border-border grid place-items-center shadow-sm">
                  {qr.qr_base64 ? (
                    <img
                      src={qr.qr_base64.startsWith("data:") ? qr.qr_base64 : `data:image/png;base64,${qr.qr_base64}`}
                      alt="QR Code WhatsApp"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="font-display font-semibold">Como conectar</div>
                    <ol className="text-sm space-y-2 mt-2 text-muted-foreground">
                      <li><strong className="text-foreground">1.</strong> Abra o WhatsApp no celular da empresa</li>
                      <li><strong className="text-foreground">2.</strong> Vá em <em>Configurações → Aparelhos conectados</em></li>
                      <li><strong className="text-foreground">3.</strong> Toque em <em>Conectar um aparelho</em></li>
                      <li><strong className="text-foreground">4.</strong> Aponte a câmera para o QR ao lado</li>
                    </ol>
                  </div>
                  <div className="text-xs flex items-center gap-2 pt-2 border-t border-border">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                    <span className="text-muted-foreground">Aguardando você escanear... (o QR expira em 60s)</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={startConnect} disabled={qrLoading} className="gap-1.5">
                    <RefreshCw className="w-3 h-3" /> Gerar novo QR
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Testar envio — só se conectado */}
      {autoAvailable && isConnected && (
        <Card className="p-5 space-y-3 rounded-tenant border-border" data-testid="wa-test-card">
          <div>
            <h4 className="font-medium flex items-center gap-2">
              <Send className="w-4 h-4" /> Enviar mensagem de teste
            </h4>
            <p className="text-xs text-muted-foreground mt-1">
              Envie uma mensagem pro seu próprio número para confirmar que está tudo OK.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="Ex: (11) 98765-4321"
              className="flex-1"
              data-testid="wa-test-phone"
            />
            <Button onClick={doTest} disabled={testing} data-testid="wa-test-btn" className="gap-2">
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Enviar teste
            </Button>
          </div>
          {testResult && (
            <div
              data-testid="wa-test-result"
              className={`p-3 rounded-md text-sm flex items-start gap-2 ${
                testResult.ok
                  ? "bg-emerald-500/10 text-emerald-700 border border-emerald-500/30"
                  : "bg-amber-500/10 text-amber-700 border border-amber-500/30"
              }`}
            >
              {testResult.ok ? (
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                {testResult.ok ? (
                  <div className="font-medium">Mensagem enviada com sucesso!</div>
                ) : (
                  <>
                    <div className="font-medium">Não conseguimos enviar</div>
                    <div className="text-xs opacity-80 mt-1 break-all">{testResult.error || testResult.message}</div>
                  </>
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Sempre-visível: fallback wa.me */}
      <div className="text-xs text-muted-foreground flex items-start gap-2 p-3 rounded-md border border-border/60 bg-muted/30">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>
          <strong>Sem preocupação:</strong> se algum envio automático falhar, o sistema abre o WhatsApp da sua empresa com a mensagem pronta pra você despachar em 1 toque. Cliente nunca fica sem receber.
        </span>
      </div>

      {/* Configuração avançada (escondida por padrão) */}
      <div className="pt-2">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors"
          data-testid="wa-advanced-toggle"
        >
          {advancedOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          Configuração avançada
          <span className="text-[10px] opacity-60">(só use se souber o que está fazendo)</span>
        </button>

        {advancedOpen && (
          <Card className="p-4 space-y-4 rounded-tenant border-border mt-3" data-testid="wa-advanced-section">
            <div className="text-xs text-muted-foreground">
              Se sua empresa já tem uma instância própria da Evolution ou da Meta Cloud API, você pode informar as credenciais aqui.
              Na maioria dos casos, <strong>não precisa mexer nisso</strong>.
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Modo de envio</Label>
              <select
                className="w-full text-sm border border-border rounded-md h-9 px-2 bg-background"
                value={cfg.provider}
                onChange={(e) => setCfg({ ...cfg, provider: e.target.value })}
                data-testid="wa-provider-select"
              >
                <option value="wa_link">Automático (usa a conexão QR acima) ou manual por link</option>
                <option value="evolution">Evolution API própria</option>
                <option value="cloud">Meta Cloud API oficial</option>
              </select>
            </div>

            {cfg.provider === "evolution" && (
              <div className="space-y-3 pt-2 border-t" data-testid="wa-evolution-section">
                <div className="space-y-1"><Label className="text-xs">URL da sua Evolution</Label>
                  <Input value={cfg.evolution.base_url}
                    onChange={(e) => setCfg({ ...cfg, evolution: { ...cfg.evolution, base_url: e.target.value } })}
                    placeholder="https://evo.suaempresa.com" data-testid="wa-evolution-base-url" /></div>
                <div className="space-y-1"><Label className="text-xs">Nome da instância</Label>
                  <Input value={cfg.evolution.instance_name}
                    onChange={(e) => setCfg({ ...cfg, evolution: { ...cfg.evolution, instance_name: e.target.value } })}
                    placeholder="minha-empresa" data-testid="wa-evolution-instance" /></div>
                <div className="space-y-1"><Label className="text-xs">Chave da instância</Label>
                  <Input type="password" value={cfg.evolution.api_key}
                    onChange={(e) => setCfg({ ...cfg, evolution: { ...cfg.evolution, api_key: e.target.value } })}
                    placeholder="deixe em branco pra preservar" data-testid="wa-evolution-key" /></div>
              </div>
            )}

            {cfg.provider === "cloud" && (
              <div className="space-y-3 pt-2 border-t" data-testid="wa-cloud-section">
                <div className="space-y-1"><Label className="text-xs">Phone Number ID (Meta)</Label>
                  <Input value={cfg.cloud.phone_number_id}
                    onChange={(e) => setCfg({ ...cfg, cloud: { ...cfg.cloud, phone_number_id: e.target.value } })}
                    data-testid="wa-cloud-phone-id" /></div>
                <div className="space-y-1"><Label className="text-xs">Access Token (Meta)</Label>
                  <Input type="password" value={cfg.cloud.access_token}
                    onChange={(e) => setCfg({ ...cfg, cloud: { ...cfg.cloud, access_token: e.target.value } })}
                    data-testid="wa-cloud-token" /></div>
                <div className="space-y-1"><Label className="text-xs">WhatsApp Business Account ID</Label>
                  <Input value={cfg.cloud.business_account_id}
                    onChange={(e) => setCfg({ ...cfg, cloud: { ...cfg.cloud, business_account_id: e.target.value } })}
                    data-testid="wa-cloud-waba" /></div>
              </div>
            )}

            <div className="flex justify-end pt-2 border-t">
              <Button variant="outline" size="sm" onClick={saveAdvanced} data-testid="wa-save-btn">
                Salvar configuração avançada
              </Button>
            </div>

            <div className="text-[11px] text-muted-foreground">
              A conexão feita pelo botão <strong>&quot;Conectar meu WhatsApp&quot;</strong> acima já cobre a maioria dos casos de uso e é atualizada sozinha.
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
